import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Edit, Trash2, ShoppingCart, ExternalLink, Package,
  Tag, DollarSign, Hash, MapPin, Barcode, AlertTriangle, Calendar,
  User, Building2, FileText, Info,
} from 'lucide-react';
import { api } from '../api/client.ts';
import { InventoryItem, Category } from '../types/index.ts';
import { useState } from 'react';
import { ItemModal, AddToOrderModal, _mdToHtml } from './Inventory.tsx';
import { getImageSrc } from '../utils/imageUrl.ts';

function getSupplyTypeFromCategory(categoryName: string | undefined): string {
  if (!categoryName) return 'other';
  const lower = categoryName.toLowerCase();
  if (lower.includes('wheelchair')) return 'wheelchair';
  if (lower.includes('concentrator')) return 'concentrators';
  if (lower.includes('lift') || lower.includes('hoyer')) return 'lift';
  if (lower.includes('bed')) return 'bed';
  if (lower.includes('hvac') || lower.includes('air handler')) return 'hvac';
  if (lower.includes('paint')) return 'paint';
  return 'other';
}

function formatUsedForTag(val: string): string {
  if (!val.includes(':')) return val;
  const parts = val.split(':');
  const type = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const brand = parts[1];
  const model = parts[2];
  const plural = type.endsWith('s') ? type : `${type}s`;
  if (!brand || brand === 'all') return `All ${plural}`;
  if (!model || model === 'all') return `${brand} ${plural}`;
  return `${brand} ${model}`;
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ color: 'var(--accent-primary)', marginTop: 1, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 2 }}>{label}</div>
        <div style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</div>
      </div>
    </div>
  );
}

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inventory-item', id],
    queryFn: () => api.get<InventoryItem>(`/inventory/items/${id}`),
    enabled: !!id,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: () => api.get<Category[]>('/inventory/categories'),
  });
  const categories: Category[] = categoriesData?.data ?? [];

  const item = data?.data;

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/inventory/items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      navigate('/inventory');
    },
  });

  const isLowStock = item?.reorder_point != null && Number(item.quantity) <= Number(item.reorder_point);

  const thumbnailSrc = getImageSrc(item?.thumbnail_path);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="empty-state">
        <Package size={48} />
        <h3>Item not found</h3>
        <p>This item may have been deleted.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/inventory')}>
          <ArrowLeft size={15} /> Back to Inventory
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Breadcrumb nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/inventory')}
          style={{ gap: 6 }}
        >
          <ArrowLeft size={14} /> Inventory
        </button>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{item.name}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, alignItems: 'start' }}>
        {/* Left: main info */}
        <div>
          {/* Header card */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700 }}>{item.name}</h2>
                  <span className={`badge ${item.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                    {item.status}
                  </span>
                  {isLowStock && (
                    <span className="badge badge-amber" style={{ display: 'inline-flex', gap: 4 }}>
                      <AlertTriangle size={11} /> Low Stock
                    </span>
                  )}
                  {item.discontinued && (
                    <span style={{
                      background: 'rgba(239,68,68,0.15)', color: '#f87171',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: 4, fontSize: 11, fontWeight: 600,
                      padding: '2px 6px',
                    }}>
                      DISCONTINUED
                    </span>
                  )}
                </div>
                {item.discontinued && item.replacement_url && (
                  <div style={{ marginTop: 4 }}>
                    <a href={item.replacement_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: '#60a5fa', textDecoration: 'none' }}>
                      → View replacement product
                    </a>
                  </div>
                )}
                {item.category_name && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {item.category_name}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowOrderModal(true)}
                  style={{ color: 'var(--accent-primary)' }}
                >
                  <ShoppingCart size={14} /> Order
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const supplyType = getSupplyTypeFromCategory(item.category_name);
                    sessionStorage.setItem('supply_prefill_data', JSON.stringify({
                      supplyType,
                      name: item.name,
                      brand: item.brand ?? '',
                      model: item.part_number ?? '',
                      imageUrl: getImageSrc(item.thumbnail_path) ?? '',
                    }));
                    navigate(`/inventory?tab=facility-supplies&sub=${supplyType}`);
                  }}
                >
                  <Package size={14} /> + Add to Inventory
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {item.product_description && (
              <div
                style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.7, fontSize: '0.875rem', maxHeight: 300, overflowY: 'auto', padding: '4px 0' }}
                dangerouslySetInnerHTML={{ __html: _mdToHtml(item.product_description) }}
              />
            )}
          </div>

          {/* Details card */}
          <div className="card">
            <h4 style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Details
            </h4>
            <DetailRow icon={<Hash size={15} />} label="Part Number" value={item.part_number} />
            <DetailRow icon={<Barcode size={15} />} label="Barcode" value={item.barcode} />
            <DetailRow icon={<DollarSign size={15} />} label="Price" value={Number(item.price) > 0 ? `$${Number(item.price).toFixed(2)}` : null} />
            <DetailRow icon={<Building2 size={15} />} label="Supplier" value={item.supplier} />
            <DetailRow icon={<Tag size={15} />} label="Brand" value={item.brand} />
            <DetailRow icon={<Tag size={15} />} label="Unit" value={`${item.unit}${item.sold_in > 1 ? ` · sold in packs of ${item.sold_in}` : ''}`} />
            <DetailRow icon={<MapPin size={15} />} label="Storage Location" value={item.location} />
            <DetailRow
              icon={<User size={15} />}
              label="Used For"
              value={Array.isArray(item.used_for) && item.used_for.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(item.used_for as string[]).map((tag) => (
                    <span key={tag} style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
                      {formatUsedForTag(tag)}
                    </span>
                  ))}
                </div>
              ) : null}
            />
            <DetailRow icon={<Building2 size={15} />} label="Department" value={item.department} />
            {item.product_link && (
              <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ color: 'var(--accent-primary)', marginTop: 1 }}><ExternalLink size={15} /></div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 2 }}>Product Link</div>
                  <a
                    href={item.product_link.startsWith('http') ? item.product_link : `https://${item.product_link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent-primary)', fontSize: '0.875rem', wordBreak: 'break-all' }}
                  >
                    {item.product_link}
                  </a>
                </div>
              </div>
            )}
            <DetailRow icon={<FileText size={15} />} label="Notes" value={item.notes ? <span dangerouslySetInnerHTML={{ __html: _mdToHtml(item.notes) }} /> : null} />
            <DetailRow
              icon={<Calendar size={15} />}
              label="Last Inventory Date"
              value={item.last_inventory_date ? new Date(item.last_inventory_date).toLocaleDateString() : null}
            />
          </div>
        </div>

        {/* Right: image + stock */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Image */}
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {thumbnailSrc ? (
              <img
                src={thumbnailSrc}
                alt={item.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Package size={64} color="var(--text-muted)" style={{ opacity: 0.3 }} />
            )}
          </div>

          {/* Stock card */}
          <div className="card">
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Stock
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Current</span>
              <span style={{
                fontWeight: 700, fontSize: '1.25rem',
                color: isLowStock ? 'var(--accent-warning)' : 'var(--text-primary)',
              }}>
                {Number(item.quantity)} <span style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--text-muted)' }}>{item.unit}</span>
              </span>
            </div>
            {item.reorder_point != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Reorder Point</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{Number(item.reorder_point)} {item.unit}</span>
              </div>
            )}
            {isLowStock && (
              <div style={{
                marginTop: 12, padding: '8px 12px',
                background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex', alignItems: 'center', gap: 6,
                color: 'var(--accent-warning)', fontSize: '0.8125rem',
              }}>
                <AlertTriangle size={13} /> Reorder needed
              </div>
            )}
          </div>

          {/* Quick info */}
          <div className="card">
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Added
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <ItemModal
          initial={item}
          categories={categories}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            void refetch();
            queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
            setShowEditModal(false);
          }}
        />
      )}

      {/* Delete confirm modal */}
      {showOrderModal && (
        <AddToOrderModal
          key={item.id}
          item={item}
          onClose={() => setShowOrderModal(false)}
        />
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Item</h3>
              <button className="modal-close" onClick={() => setConfirmDelete(false)}><Info size={18} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Permanently delete <strong>{item.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={() => deleteMut.mutate()}
                disabled={deleteMut.isPending}
              >
                <Trash2 size={15} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
